<template>
  <el-dialog
    :visible.sync="dialogVisible"
    :title="$locale('trans.report.action.refunded')"
    width="1200px"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :show-close="true"
    custom-class="multi-select-refund-dialog"
    @opened="applyDefaultSelection"
  >
    <!-- 基本信息 -->
    <div class="refund-basic-info">
      <span class="info-cell"><label>取引番号：</label>{{ searchIds || '-' }}</span>
      <span class="info-cell"><label>作品名：</label>{{ filmNames || '-' }}</span>
      <span class="info-cell"><label>払い戻し合計：</label>{{ formatAmount(selectedTotal) }}</span>
    </div>

    <el-table
      ref="refundTable"
      class="egl-table list-table"
      :data="flatRows"
      row-key="transactionItemId"
      :tree-props="{ children: 'noTreeChildren', hasChildren: 'noTreeHasChildren' }"
      size="small"
      max-height="420"
      @select="onSelect"
      @selection-change="onSelectionChange"
    >
      <el-table-column type="selection" :selectable="isSelectable" width="55" align="center" />
      <el-table-column label="券種" min-width="220">
        <template slot-scope="{ row }">
          <span :class="{ 'refund-child-name': row.type !== 'ticket' }">
            <span v-if="row.type !== 'ticket'" class="refund-child-mark">└&nbsp;</span>{{ row.itemName }}
            <span v-if="row.type === 'ticket' && row.bundleType !== 'NONE'" class="item-bundle">[{{ row.bundleType }}]</span>
          </span>
        </template>
      </el-table-column>
      <el-table-column label="取引番号" min-width="150">
        <template slot-scope="{ row }">{{ row.searchId ? formatSearchId(row.searchId) : '' }}</template>
      </el-table-column>
      <el-table-column label="作品名" prop="filmName" min-width="220" />
      <el-table-column label="上映日付" min-width="130">
        <template slot-scope="{ row }">{{ row.scheduleStartTime ? $moment(row.scheduleStartTime).format('YYYY-MM-DD') : '' }}</template>
      </el-table-column>
      <el-table-column label="上映時間" min-width="110">
        <template slot-scope="{ row }">{{ row.scheduleStartTime ? $moment(row.scheduleStartTime).format('HH:mm') : '' }}</template>
      </el-table-column>
      <el-table-column label="座席" prop="seatName" min-width="120" />
      <el-table-column label="価格" align="right" min-width="130">
        <template slot-scope="{ row }">{{ formatAmount(row.amount) }}</template>
      </el-table-column>
    </el-table>

    <span slot="footer" class="dialog-footer">
      <el-button
        class="egl-btn-white"
        @click="onCancel"
        data-testid="multi-refund-cancel"
      >{{ $locale('global.cancel') }}</el-button>
      <el-button
        type="warning"
        class="egl-btn-yellow"
        @click="onConfirm"
        data-testid="multi-refund-confirm"
      >{{ $locale('trans.report.action.refunded') }}</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { formatStringFixed } from "@/utils/common";

export default {
  name: "MultiSelectRefundDialog",
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    items: {
      type: Array,
      default: () => []
    },
    // The initially-selected transactionItemId (the one user clicked refund on)
    defaultSelected: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      selectedRows: [],
      // Guards selection-change while we programmatically toggle DISCOUNT/COMBO siblings.
      suppressInterlock: false
    };
  },
  computed: {
    // Bridge el-dialog's visible to the parent without mutating the prop directly.
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(val) {
        if (!val) {
          this.$emit("cancel");
        }
      }
    },
    // tickets, each carrying its combo-bundled goods as children, then flattened:
    // ticket row, then its bundled goods right after it (indented in the UI).
    // IMPORTANT: built ONCE here so every row object identity is stable. el-table tracks
    // selection by these exact references; rebuilding them elsewhere (a separate `ticketRows`
    // computed) hands the table/`toggleRowSelection` fresh objects it doesn't hold, which
    // corrupts the internal selection array and leaves `selectedRows` stale (total won't reset).
    flatRows() {
      const rows = [];
      this.items
        .filter(i => i.type === "ticket")
        .forEach(t => {
          // NOTE: do not name this `children` — el-table treats a `children` field as tree data
          // (with row-key set) and renders an expand arrow. Use `bundledGoods` instead.
          const bundledGoods = this.items.filter(g => g.type !== "ticket" && g.bundleId === t.transactionItemId);
          const ticket = { ...t, bundledGoods };
          rows.push(ticket);
          bundledGoods.forEach(g => rows.push(g));
        });
      return rows;
    },
    // ticket rows only — derived from flatRows so references match what el-table holds.
    ticketRows() {
      return this.flatRows.filter(r => r.type === "ticket");
    },
    // 取引番号 / 作品名 reflect the CURRENT selection only — empty when nothing is selected,
    // so they stay consistent with the ¥0 total (the template shows '-' when blank).
    selectedItems() {
      const out = [];
      this.selectedRows.forEach(row => {
        out.push(row);
        (row.bundledGoods || []).forEach(g => out.push(g));
      });
      return out;
    },
    searchIds() {
      return [ ...new Set(this.selectedItems.map(i => i.searchId).filter(Boolean)) ].join("、");
    },
    filmNames() {
      return [ ...new Set(this.selectedItems.map(i => i.filmName).filter(Boolean)) ].join("、");
    },
    // Live refund total = checked tickets + their bundled goods.
    selectedTotal() {
      let total = 0;
      this.selectedRows.forEach(row => {
        total += row.amount || 0;
        (row.bundledGoods || []).forEach(g => { total += g.amount || 0; });
      });
      return total;
    }
  },
  methods: {
    isSelectable(row) {
      return row.type === "ticket";
    },
    applyDefaultSelection() {
      const table = this.$refs.refundTable;
      if (!table) return;
      // Reset our own mirror first; clearSelection() doesn't reliably fire selection-change.
      this.selectedRows = [];
      table.clearSelection();
      const clicked = this.ticketRows.find(r => r.transactionItemId === this.defaultSelected);
      if (clicked) {
        table.toggleRowSelection(clicked, true);
      }
    },
    onSelect(selection, row) {
      if (row.type !== "ticket") return;
      // Match by row-key, not reference — selection payload identity isn't guaranteed.
      const isSelected = selection.some(r => r.transactionItemId === row.transactionItemId);
      // Interlock COMBO/DISCOUNT tickets sharing the same bundleId (check/uncheck together).
      // For DISCOUNT, each ticket is a separate row carrying its own amount, so a sibling that
      // fails to toggle would make the total under-count.
      if (row.bundleType !== "NONE" && row.bundleId) {
        this.suppressInterlock = true;
        this.ticketRows.forEach(other => {
          if (other.transactionItemId !== row.transactionItemId
            && other.bundleType === row.bundleType
            && other.bundleId === row.bundleId) {
            this.$refs.refundTable.toggleRowSelection(other, isSelected);
          }
        });
        this.suppressInterlock = false;
        // toggleRowSelection during a select event can leave selection-change carrying a partial
        // payload (event-ordering race). Recompute selectedRows from the table's authoritative
        // state on the next tick so DISCOUNT siblings are always fully counted.
        this.$nextTick(() => this.syncSelectedRows());
      }
    },
    onSelectionChange(selection) {
      // Ignore the intermediate selection-change fired by our own interlock toggling;
      // onSelect's $nextTick(syncSelectedRows) provides the final authoritative state.
      if (this.suppressInterlock) return;
      this.selectedRows = selection.filter(r => r.type === "ticket");
    },
    // Read the table's real selection (by row-key) instead of trusting an event payload.
    syncSelectedRows() {
      const table = this.$refs.refundTable;
      if (!table) return;
      const selected = (table.selection || []).filter(r => r.type === "ticket");
      // Map back to flatRows references so bundledGoods/amount are intact.
      const ids = new Set(selected.map(r => r.transactionItemId));
      this.selectedRows = this.ticketRows.filter(r => ids.has(r.transactionItemId));
    },
    onConfirm() {
      // Only tickets are submittable; backend re-includes combo bundled goods automatically.
      this.$emit("confirm", this.selectedRows);
    },
    onCancel() {
      this.$emit("cancel");
    },
    formatAmount(amount) {
      return "¥" + (amount || 0).toLocaleString();
    },
    formatSearchId(searchId) {
      // 与列表页一致：取引番号(searchId) 走 formatStringFixed
      return searchId ? formatStringFixed(searchId) : '';
    }
  }
};
</script>

<style scoped>
.refund-basic-info {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 24px;
  margin-bottom: 12px;
  font-size: 14px;
  color: #333;
}
.refund-basic-info .info-cell label {
  color: #909399;
}
.refund-dialog-hint {
  margin-bottom: 12px;
  color: #e6a23c;
  font-size: 13px;
}
.item-bundle {
  color: #e6a23c;
  font-size: 12px;
  margin-left: 4px;
}
.refund-child-name {
  color: #606266;
  padding-left: 18px;
}
.refund-child-mark {
  color: #c0c4cc;
}
</style>
